import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

const CollaborationPanel = ({ document, onUpdate }) => {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (document?.id) {
      loadCollaborationData();
    }
  }, [document?.id]);

  const loadCollaborationData = async () => {
    try {
      setLoading(true);
      setError('');

      const [commentsResult, approvalsResult] = await Promise.all([
        supabase
          ?.from('document_comments')
          ?.select(`
            *,
            user_profiles!document_comments_user_id_fkey(full_name, email, avatar_url)
          `)
          ?.eq('document_id', document?.id)
          ?.order('created_at', { ascending: false }),
        supabase
          ?.from('document_approvals')
          ?.select(`
            *,
            user_profiles!document_approvals_reviewer_id_fkey(full_name, email)
          `)
          ?.eq('document_id', document?.id)
          ?.order('created_at', { ascending: false })
      ]);

      if (commentsResult?.error) throw commentsResult?.error;
      if (approvalsResult?.error) throw approvalsResult?.error;

      setComments(commentsResult?.data || []);
      setApprovals(approvalsResult?.data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load collaboration data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async (e) => {
    e?.preventDefault();
    if (!newComment?.trim() || !user) return;

    try {
      setSubmitting(true);
      setError('');

      const { error: insertError } = await supabase
        ?.from('document_comments')
        ?.insert({
          document_id: document?.id,
          user_id: user?.id,
          content: newComment?.trim(),
          is_resolved: false
        });

      if (insertError) throw insertError;

      setNewComment('');
      await loadCollaborationData();
      onUpdate?.();
    } catch (err) {
      setError(err?.message || 'Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleResolved = async (commentId, currentStatus) => {
    try {
      const { error: updateError } = await supabase
        ?.from('document_comments')
        ?.update({ is_resolved: !currentStatus })
        ?.eq('id', commentId);

      if (updateError) throw updateError;

      await loadCollaborationData();
    } catch (err) {
      setError(err?.message || 'Failed to update comment');
    }
  };

  const getApprovalStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'var(--color-success)';
      case 'rejected':
        return 'var(--color-destructive)';
      case 'revision_requested':
        return 'var(--color-warning)';
      default:
        return 'var(--color-muted-foreground)';
    }
  };

  const getApprovalStatusLabel = (status) => {
    const labels = {
      pending: 'Pending Review',
      approved: 'Approved',
      rejected: 'Rejected',
      revision_requested: 'Revision Requested'
    };
    return labels?.[status] || status;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
          <p className="text-sm text-muted-foreground">Loading collaboration data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Approval Status */}
      {approvals?.length > 0 && (
        <div className="card">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex items-center justify-center w-8 h-8 bg-primary/10 rounded-lg">
              <Icon name="CheckSquare" size={18} color="var(--color-primary)" />
            </div>
            <h3 className="text-lg font-semibold">Approval Status</h3>
          </div>

          <div className="space-y-3">
            {approvals?.map(approval => (
              <div key={approval?.id} className="p-4 bg-background rounded-lg border border-border">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Icon name="User" size={16} className="text-muted-foreground" />
                    <span className="text-sm font-medium">
                      {approval?.user_profiles?.full_name || 'Unknown Reviewer'}
                    </span>
                  </div>
                  <span
                    className="px-2 py-1 text-xs rounded font-medium"
                    style={{
                      backgroundColor: `${getApprovalStatusColor(approval?.status)}15`,
                      color: getApprovalStatusColor(approval?.status)
                    }}
                  >
                    {getApprovalStatusLabel(approval?.status)}
                  </span>
                </div>
                {approval?.comments && (
                  <p className="text-sm text-muted-foreground mt-2">{approval?.comments}</p>
                )}
                <p className="text-xs text-muted-foreground mt-2">
                  {approval?.reviewed_at
                    ? `Reviewed ${new Date(approval?.reviewed_at)?.toLocaleString()}`
                    : `Requested ${new Date(approval?.created_at)?.toLocaleString()}`
                  }
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Comments Section */}
      <div className="card">
        <div className="flex items-center gap-3 mb-4">
          <div className="flex items-center justify-center w-8 h-8 bg-primary/10 rounded-lg">
            <Icon name="MessageSquare" size={18} color="var(--color-primary)" />
          </div>
          <h3 className="text-lg font-semibold">Comments & Discussion</h3>
        </div>

        <form onSubmit={handleAddComment} className="mb-4">
          <div className="flex gap-2">
            <Input
              placeholder="Add a comment..."
              value={newComment}
              onChange={(e) => setNewComment(e?.target?.value)}
              disabled={submitting}
              className="flex-1"
            />
            <Button
              type="submit"
              disabled={!newComment?.trim() || submitting}
              loading={submitting}
              iconName="Send"
            >
              Post
            </Button>
          </div>
        </form>

        <div className="space-y-3">
          {comments?.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Icon name="MessageSquare" size={48} className="mx-auto mb-3 opacity-20" />
              <p className="text-sm">No comments yet. Start the discussion!</p>
            </div>
          ) : (
            comments?.map(comment => (
              <div
                key={comment?.id}
                className={`
                  p-4 rounded-lg border transition-all
                  ${comment?.is_resolved
                    ? 'bg-success/5 border-success/20 opacity-60' :'bg-background border-border'
                  }
                `}
              >
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center w-8 h-8 bg-primary/10 rounded-full flex-shrink-0">
                    <Icon name="User" size={16} color="var(--color-primary)" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="text-sm font-medium">
                          {comment?.user_profiles?.full_name || 'Unknown User'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(comment?.created_at)?.toLocaleString()}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName={comment?.is_resolved ? 'RotateCcw' : 'Check'}
                        onClick={() => handleToggleResolved(comment?.id, comment?.is_resolved)}
                      >
                        {comment?.is_resolved ? 'Reopen' : 'Resolve'}
                      </Button>
                    </div>
                    <p className="text-sm text-foreground">{comment?.content}</p>
                    {comment?.is_resolved && (
                      <div className="mt-2 flex items-center gap-1 text-xs text-success">
                        <Icon name="CheckCircle" size={12} />
                        <span>Resolved</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default CollaborationPanel;